/**
 * Call Service
 * Handles API calls for video/audio calling with Daily.co
 */

import { getBackendUrl } from '../config';

const API_URL = getBackendUrl();

class CallService {
  async getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Check if calling is enabled and get config
   */
  async getConfig() {
    const response = await fetch(`${API_URL}/api/calls/config`, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      return { enabled: false };
    }
    
    return response.json();
  }

  /**
   * Initiate a new call
   * @param {Object} options - Call options
   * @param {string} options.clientId - Client ID (for agent-initiated calls)
   * @param {string} options.conversationId - Related chat conversation ID
   * @param {string} options.callType - 'video' or 'audio'
   */
  async initiateCall({ clientId, conversationId, callType = 'video' }) {
    const response = await fetch(`${API_URL}/api/calls/initiate`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        client_id: clientId,
        conversation_id: conversationId,
        call_type: callType
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to initiate call');
    }
    
    return response.json();
  }

  /**
   * Join an existing call (answer incoming call)
   * @param {string} callId - Call ID to join
   */
  async joinCall(callId) {
    const response = await fetch(`${API_URL}/api/calls/join/${callId}`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to join call');
    }
    
    return response.json();
  }

  /**
   * End an active call
   * @param {string} callId - Call ID to end
   */
  async endCall(callId) {
    const response = await fetch(`${API_URL}/api/calls/end/${callId}`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to end call');
    }
    
    return response.json();
  }

  /**
   * Decline an incoming call
   * @param {string} callId - Call ID to decline
   */
  async declineCall(callId) {
    const response = await fetch(`${API_URL}/api/calls/decline/${callId}`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to decline call');
    }
    
    return response.json();
  }

  /**
   * Get call history
   * @param {number} limit - Maximum number of calls to return
   */
  async getCallHistory(limit = 50) {
    const response = await fetch(`${API_URL}/api/calls/history?limit=${limit}`, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch call history');
    }
    
    return response.json();
  }

  /**
   * Get missed calls (for agents)
   */
  async getMissedCalls() {
    const response = await fetch(`${API_URL}/api/calls/missed`, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      return { missed_calls: [] };
    }
    
    return response.json();
  }

  /**
   * Check for active call
   */
  async getActiveCall() {
    const response = await fetch(`${API_URL}/api/calls/active`, {
      headers: await this.getAuthHeaders()
    });
    
    if (!response.ok) {
      return { active_call: null };
    }
    
    return response.json();
  }
}

export const callService = new CallService();
export default callService;
